import { useOthers, useSelf, useUpdateMyPresence } from "@configs/liveblocks.config"
import { atom, useAtom } from "jotai"
import { atomWithStorage } from "jotai/utils"
import { useEffect } from "react"

import { initCrossTabSession } from "@utils/initCrossTabSession"

import { COLLABORATION_USER_COLORS } from "@constants/storage"

const COLLABORATION_PALETTE = [
    "var(--live-collaboration-crimson)",
    "var(--live-collaboration-pink)",
    "var(--live-collaboration-plum)",
    "var(--live-collaboration-violet)",
    "var(--live-collaboration-indigo)",
    "var(--live-collaboration-blue)",
    "var(--live-collaboration-cyan)",
    "var(--live-collaboration-teal)",
    "var(--live-collaboration-orange)",
    "var(--live-collaboration-amber)",
]

type UserColor = {
    userId: string
    color: string
}

const userColorsAtom = atomWithStorage<UserColor[]>(COLLABORATION_USER_COLORS, [])

const myColorAtom = atom<string | null>(null)

function pickColor(userId: string, assignments: UserColor[]): string {
    const assigned = assignments.find((assignment) => assignment.userId === userId)
    if (assigned) {
        return assigned.color
    } else {
        const usedColors = assignments.map((assignment) => assignment.color)
        const available = COLLABORATION_PALETTE.filter((color) => !usedColors.includes(color))
        const pool = available.length > 0 ? available : COLLABORATION_PALETTE
        return pool[Math.floor(Math.random() * pool.length)]
    }
}

function getActiveUserIds(selfId: string, others: { id: string }[]): string[] {
    return [selfId, ...others.map((user) => String(user.id))]
}

function filterAssignmentsByActiveUsers(assignments: UserColor[], activeUserIds: string[]): UserColor[] {
    return assignments.filter((assignment) => activeUserIds.includes(String(assignment.userId)))
}

export function useCollaborationColors() {
    const self = useSelf()

    const [assignments, setAssignments] = useAtom(userColorsAtom)
    const [myColor, setMyColor] = useAtom(myColorAtom)

    const updatePresence = useUpdateMyPresence()

    const others = useOthers()

    useEffect(() => {
        const cleanup = initCrossTabSession({
            onNewSession: () => setAssignments([]),
        })

        return cleanup
    }, [])

    useEffect(() => {
        if (!self?.id) {
            return
        }

        const activeUserIds = getActiveUserIds(
            self.id,
            others.map(({ id }) => ({ id: String(id) })),
        )

        const updatedAssignments = filterAssignmentsByActiveUsers(assignments, activeUserIds)

        others.forEach(({ id, presence }) => {
            const userId = String(id)
            const alreadyAssigned = updatedAssignments.some((assignment) => assignment.userId === userId)
            if (presence?.color && !alreadyAssigned) {
                updatedAssignments.push({ userId, color: presence.color })
            }
        })

        const myColor = pickColor(self.id, updatedAssignments)
        setMyColor(myColor)
        updatePresence({ color: myColor })

        const myAssignmentExists = updatedAssignments.some((assignment) => assignment.userId === self.id)
        if (!myAssignmentExists) {
            updatedAssignments.push({ userId: self.id, color: myColor })
        }

        const hasChanged =
            updatedAssignments.length !== assignments.length ||
            !updatedAssignments.every(({ userId, color }) =>
                assignments.some(
                    (existingAssignment) => existingAssignment.userId === userId && existingAssignment.color === color,
                ),
            )

        if (hasChanged) {
            setAssignments(updatedAssignments)
        }
    }, [self?.id, others, assignments, setAssignments, updatePresence, setMyColor])

    const getUserColor = (userId: string): string | null => {
        const found = assignments.find((assignment) => assignment.userId === userId)
        return found ? found.color : null
    }

    return {
        myColor,
        getUserColor,
        allAssignments: assignments,
    }
}
